import { useEffect, useState, useMemo } from 'react'
import { BottomSheet } from './BottomSheet'
import { Button } from './Button'
import { useNls } from '../../nls/nlsProvider'
import {
  registerSwitchGuardSheetPrompt,
  type SwitchGuardPromptRequest,
} from '../../services/workspaceSwitchService'

/**
 * Shared unsaved work guard sheet for workspace switching (WS5, S2).
 * Displays blocking work (counts, scans, forms, manage drafts) with:
 * - Save and switch (hidden when no guard can save)
 * - Discard and switch
 * - Stay
 */
export const WorkspaceSwitchGuardSheet = () => {
  const { t } = useNls()
  const [request, setRequest] = useState<SwitchGuardPromptRequest | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    return registerSwitchGuardSheetPrompt((req) => {
      setRequest(req)
    })
  }, [])

  const blockingLines = useMemo(() => {
    if (!request) return []
    const { fromWorkspaceId, toWorkspaceId, blocking } = request
    return blocking
      .map((g) => g.describe(fromWorkspaceId ?? '', toWorkspaceId ?? ''))
      .filter((line): line is string => Boolean(line))
  }, [request])

  const canSave = useMemo(() => {
    if (!request) return false
    return request.blocking.some((g) => typeof g.save === 'function')
  }, [request])

  const handleStay = () => {
    if (!request || isBusy) return
    const pending = request
    setRequest(null)
    void pending.resolve('STAY')
  }

  const handleDiscard = async () => {
    if (!request || isBusy) return
    const pending = request
    setIsBusy(true)
    try {
      await pending.resolve('DISCARD')
      setRequest(null)
    } finally {
      setIsBusy(false)
    }
  }

  const handleSave = async () => {
    if (!request || isBusy) return
    const pending = request
    setIsBusy(true)
    try {
      const result = await pending.resolve('SAVE')
      if (result && result.ok === false) {
        // On save error: keep sheet open, stay, re-enable buttons
        return
      }
      setRequest(null)
    } finally {
      setIsBusy(false)
    }
  }

  if (!request) return null

  return (
    <BottomSheet
      isOpen={Boolean(request)}
      onClose={handleStay}
      title={t('NLSWorkspaceSwitchGuardTitle')}
      className="memberBottomSheet switchGuardSheet"
      maxHeightSvh={40}
    >
      <div
        className="p-4 flex flex-col gap-4 text-[var(--text-strong)]"
        data-testid="workspace-switch-guard-sheet"
      >
        <div data-testid="switch-guard-sheet" className="flex flex-col gap-3">
          <p className="text-sm text-[var(--muted)]">{t('NLSWorkspaceSwitchGuardBody')}</p>
          {blockingLines.length > 0 && (
            <ul className="list-disc pl-5 text-sm space-y-1">
              {blockingLines.map((line, idx) => (
                <li key={idx}>{line}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="tertiary" size="md" disabled={isBusy} onClick={handleStay}>
            {t('NLSWorkspaceSwitchGuardStay')}
          </Button>
          <Button variant="secondary" tone="danger" size="md" disabled={isBusy} onClick={handleDiscard}>
            {t('NLSWorkspaceSwitchGuardDiscard')}
          </Button>
          {canSave && (
            <Button
              variant="primary"
              size="md"
              disabled={isBusy}
              onClick={handleSave}
            >
              {t('NLSWorkspaceSwitchGuardSave')}
            </Button>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
